import React, { createContext, FC, RefObject, useEffect, useRef, useState, JSX, forwardRef, ComponentType, PropsWithChildren, Ref, RefAttributes } from "react";
import { commonFocusableElements, focusFirstInteractiveElement } from "../utility";
import { handleCssTransition } from "../qes-standards/utility/handleCssTransition";

interface FlyoutContextIncomingProps {
  children: React.ReactNode;
  ignoreDomUpdates?: boolean;
  title?: string | JSX.Element;
  warnOnClose?: boolean;
}

export interface FlyoutContextOutgoingProps {
  cancelClose: () => void;
  confirmClose: () => void;
  readonly firstFocusableElRef: RefObject<HTMLElement>;
  readonly flyoutBodyRef: RefObject<HTMLDivElement>;
  readonly flyoutRef: RefObject<HTMLDivElement>;
  readonly hasOpened: boolean;
  handleCloseFlyout: () => Promise<unknown>;
  handleOpen: () => void;
  handleOpenMenu: () => void;
  readonly isFlyoutMounted: boolean;
  readonly isFlyoutVisible: boolean;
  readonly isModalWarningVisible: boolean;
  readonly lastFocusableElRef: RefObject<HTMLElement>;
  setIsModalWarningVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setIsFlyoutVisible: React.Dispatch<React.SetStateAction<boolean>>;
  setSuspendEscape: React.Dispatch<React.SetStateAction<boolean>>;
  setTitle: React.Dispatch<React.SetStateAction<string | JSX.Element>>;
  setTriggerElement: React.Dispatch<React.SetStateAction<RefObject<HTMLElement>>>;
  readonly title: string | JSX.Element;
  triggerElement: RefObject<HTMLElement>;
  updateFocusableEls: () => void;
}

const initialValues: FlyoutContextOutgoingProps = {
  cancelClose: () => null,
  confirmClose: () => null,
  firstFocusableElRef: null,
  flyoutBodyRef: null,
  flyoutRef: null,
  handleCloseFlyout: () => null,
  handleOpen: () => null,
  handleOpenMenu: () => null,
  hasOpened: false,
  isFlyoutMounted: false,
  isFlyoutVisible: false,
  isModalWarningVisible: false,
  lastFocusableElRef: null,
  setIsFlyoutVisible: () => null,
  setIsModalWarningVisible: () => null,
  setSuspendEscape: () => null,
  setTitle: () => null,
  setTriggerElement: () => null,
  title: "",
  triggerElement: null,
  updateFocusableEls: () => null,
}

export const FlyoutContext = createContext(initialValues);

const FlyoutContextProvider: FC<FlyoutContextIncomingProps> = (props: FlyoutContextIncomingProps) => {
  const [isFlyoutMounted, setIsFlyoutMounted] = useState(initialValues.isFlyoutMounted);
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(initialValues.isFlyoutVisible);
  const [isModalWarningVisible, setIsModalWarningVisible] = useState(initialValues.isModalWarningVisible);
  const [suspendEscape, setSuspendEscape] = useState(false);
  const [title, setTitle] = useState<string | JSX.Element>(props.title || initialValues.title);
  const [triggerElement, setTriggerElement] = useState<RefObject<HTMLElement>>();
  const flyoutRef = useRef<HTMLDivElement>(null);
  const flyoutBodyRef = useRef<HTMLDivElement>(null);
  const firstFocusableElRef = useRef<HTMLElement>(null);
  const lastFocusableElRef = useRef<HTMLElement>(null);
  const hasOpenedRef = useRef(initialValues.hasOpened);
  // should match the transition duration in _new-flyout.scss
  const transitionDuration = 300;

  useEffect(() => {
    setTitle(props.title);
  }, [props.title]);

  useEffect(() => {
    if (hasOpenedRef.current) {
      if (!isFlyoutVisible) {
        if (triggerElement && !!triggerElement.current && !!triggerElement.current.offsetParent) {
          triggerElement.current.focus();
        } else {
          focusFirstInteractiveElement();
        }
      }
    }
  }, [isFlyoutVisible]);

  // in case setIsFlyoutVisible was called directly instead of handleOpen
  // because we're silly like that sometimes
  useEffect(() => {
    if (isFlyoutVisible && !isFlyoutMounted) {
      handleOpen();
      handleCssTransition(flyoutRef, "new-flyout", true);
    }
  }, [isFlyoutVisible, isFlyoutMounted]);

  const handleOpen = () => {
    setIsFlyoutMounted(true);
    setIsFlyoutVisible(true);
    hasOpenedRef.current = true;
  }

  const handleOpenMenu = () => {
    setIsFlyoutMounted(true);
    setIsFlyoutVisible(true);
    hasOpenedRef.current = false;
  }

  const handleCloseFlyout = () => {
    if (props.warnOnClose) {
      setIsModalWarningVisible(true);
    } else {
      setIsFlyoutVisible(false);
      // returning a promise with a timeout lets context consumers optionally call
      // flyoutContext.handleCloseFlyout.then(something) to guarantee the flyout
      // closing animation plays before it goes and updates a parent's state
      // that could abruptly derender a flyout
      // centralizing this here also means the magic transitionDuration is only declared once
      // instead of in every component that needs the delay
      return new Promise((resolve) => window.setTimeout(resolve, transitionDuration));
    }
  }

  const confirmClose = () => {
    setIsFlyoutVisible(false);
    setIsModalWarningVisible(false);
    return new Promise((resolve) => window.setTimeout(resolve, transitionDuration));
  }

  const cancelClose = () => {
    setIsModalWarningVisible(false);
  }

  // This should be called by the content whenever there's a DOM change
  const updateFocusableEls = () => {
    // we need to trap focus in the flyout
    // implementation based on this: https://hidde.blog/using-javascript-to-trap-focus-in-an-element/
    if (isFlyoutVisible && flyoutRef.current) {
      const focusableEls = flyoutRef.current.querySelectorAll(commonFocusableElements);
      firstFocusableElRef.current = focusableEls[0] as HTMLElement;
      lastFocusableElRef.current = focusableEls[focusableEls.length - 1] as HTMLElement;

      // if the current focus is outside the flyout, move it inside the flyout
      if (document.activeElement.closest(flyoutRef.current.className.replaceAll(" ", ".")) === null) {
        // but get the first one in the body instead of the close button if we can help it
        const firstFocusableBodyEl = flyoutBodyRef.current.querySelector(commonFocusableElements);
        (firstFocusableBodyEl as HTMLElement || firstFocusableElRef.current).focus();
      }
    }
  }

  useEffect(() => {
    updateFocusableEls();
  }, [isFlyoutVisible]);

  useEffect(() => {
    if (isFlyoutMounted && flyoutRef.current) {
      handleCssTransition(flyoutRef, "new-flyout", !isFlyoutVisible);

      if (!isFlyoutVisible) {
        window.setTimeout(() => { setIsFlyoutMounted(false) }, transitionDuration);
      }
    }
  }, [isFlyoutVisible, isFlyoutMounted]);

  useEffect(() => {
    const observer = new MutationObserver(updateFocusableEls);
    if (isFlyoutVisible && flyoutBodyRef.current && !props.ignoreDomUpdates) {
      // we need to watch for dom node changes inside the flyout, but not for metadata like attributes
      observer.observe(flyoutBodyRef.current, { childList: true });
    } else {
      observer.disconnect();
    }

    return () => {
      observer.disconnect();
    }
  }, [isFlyoutVisible, flyoutBodyRef.current, props.ignoreDomUpdates]);

  useEffect(() => {
    const keyCatch = (e: KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          if (!suspendEscape) {
            handleCloseFlyout();
          }
          break;
        case "Tab":
          // don't let the user tab out of this flyout
          if (e.shiftKey) {
            if (document.activeElement === firstFocusableElRef.current) {
              lastFocusableElRef.current?.focus();
              e.preventDefault();
            }
          } else {
            if (document.activeElement === lastFocusableElRef.current) {
              firstFocusableElRef.current?.focus();
              e.preventDefault();
            }
          }
          break;
      }
    }

    if (isFlyoutVisible) {
      window.addEventListener("keydown", keyCatch);
    } else {
      window.removeEventListener("keydown", keyCatch);
    }

    return () => {
      window.removeEventListener("keydown", keyCatch);
    };
    /* warnOnClose needs to be here because keyCatch
        calls handleCloseFlyout, and handleCloseFlyout
        relies on it, and needs it up to date
     */
  }, [isFlyoutVisible, props.warnOnClose, suspendEscape]);

  return (
    <FlyoutContext.Provider
      value={{
        cancelClose,
        confirmClose,
        firstFocusableElRef,
        flyoutBodyRef,
        flyoutRef,
        handleCloseFlyout,
        handleOpen,
        handleOpenMenu,
        hasOpened: hasOpenedRef.current,
        isFlyoutMounted,
        isFlyoutVisible,
        isModalWarningVisible,
        lastFocusableElRef,
        setIsFlyoutVisible,
        setIsModalWarningVisible,
        setSuspendEscape,
        setTitle,
        setTriggerElement,
        title,
        triggerElement,
        updateFocusableEls,
      }}>
      {props.children}
    </FlyoutContext.Provider>
  )
}

export function withFlyoutContext<T, R= unknown>(
  Component: ComponentType<T & FlyoutContextOutgoingProps & { ref?: Ref<R> }>
): React.ComponentType<T & React.PropsWithChildren<unknown> & React.RefAttributes<R>> {

  const WrappedFlyoutContextProviderComponent = forwardRef<R, T & PropsWithChildren<unknown>>((props, ref) => {
    return (
      <FlyoutContext.Consumer>
        {(context) => (
          <Component 
            {...(props as T)} 
            {...(context as FlyoutContextOutgoingProps)} 
            ref={ref}
          />
        )}
      </FlyoutContext.Consumer>
    );
  });

  WrappedFlyoutContextProviderComponent.displayName = `withFlyoutContext(${Component.displayName || Component.name || 'Component'})`;

  return WrappedFlyoutContextProviderComponent as ComponentType<T & PropsWithChildren<unknown> & RefAttributes<R>>;
  
}

export default FlyoutContextProvider;